import React from 'react';
import { BsArchive, BsChevronDown, BsLink45Deg, BsPencil, BsTrash } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Flex, Icon, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import IconButtonMenu from '../../../../../../../../../../../../../components/IconButtonMenu';
import { ConfirmActionWarningModal } from '../../../../../../../../../../../../../components/library';
import StackedButtonGroup from '../../../../../../../../../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { analyticsIds } from '../../../../../../../../../../../../../constants/analyticsIds';
import { CypressIds } from '../../../../../../../../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../../../../../../../../constants/inAppText';
import { generateS3GetUrl } from '../../../../../../../../../../../../../redux/modules/documents';
import { hideModal, showModal, updateModalOptions } from '../../../../../../../../../../../../../redux/modules/modal';
import { useArchiveDocument, useDeleteDocument } from '../../../../../../../../../../../../../services/document-service';
import { showDontAskAgainModal } from '../../../../../../../../../../../../../utils/askAgainModalUtil';
import DocumentExpirationDateModal from '../components/DocumentExpirationDateModal';
import EditDocumentNameModal from '../components/EditDocumentNameModal';

import BaseDocumentCell from './BaseDocumentCell';

const FileNameCell = ({ document, recordId, hideOptions = false }) => {
  const dispatch = useDispatch();
  const { mutate: archiveDocument } = useArchiveDocument({
    docId: document?.id,
    insuredId: document?.insurableId,
    recordId,
    docTypeId: document?.insuranceDocumentTypeId
  });
  const { mutate: deleteDocument } = useDeleteDocument({
    docId: document?.id,
    insuredId: document?.insurableId,
    recordId,
    docTypeId: document?.insuranceDocumentTypeId
  });

  if (!document) return null;

  const handleDocClick = async docId => {
    const getUrl = await dispatch(generateS3GetUrl(docId));
    window.open(getUrl, '_blank');
  };

  const menuOptions = [
    {
      label: 'Edit Expiration Date',
      icon: BsLink45Deg,
      dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.openLinkDocumentToPolicyModal,
      cypressId: CypressIds.documentOptionsLinkToPolicyOption(document.id),
      handler: () => {
        dispatch(showModal(
          DocumentExpirationDateModal,
          {
            title: 'Edit Expiration Date',
            size: '2xl',
            center: false
          },
          { document, recordId }
        ));
      }
    },
    {
      label: 'Edit Name',
      icon: BsPencil,
      dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.openEditNameModal,
      cypressId: CypressIds.documentOptionsEditNameOption(document.id),
      handler: () => {
        dispatch(showModal(() => <EditDocumentNameModal docId={document.id} insuredId={document.insurableId} jobId={document.jobId} recordId={recordId} />, {
          actionRequired: true,
          size: 'md',
          title: 'Custom Document Name'
        }));
      }
    },
    {
      label: 'Archive Document',
      icon: BsArchive,
      dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.archiveDocument,
      cypressId: CypressIds.documentOptionsArchiveOption(document.id),
      handler: () => {
        showDontAskAgainModal({
          dispatch,
          cookieId: 'archive-document-cookie',
          title: 'Archive Document?',
          description: 'You can reactivate the document at any time.',
          onClick: async () => {
            dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: true } } }));
            await archiveDocument();

            dispatch(hideModal());
            dispatch(updateModalOptions({ buttons: { primaryButtonOptions: { isLoading: false } } }));
          }
        });
      }
    },
    {
      label: 'Delete Document',
      icon: BsTrash,
      dataAnalyticsId: analyticsIds.insuredProfilePage.documentsComponent.deleteDocument,
      cypressId: CypressIds.documentOptionsDeleteOption(document.id),
      handler: () => {
        dispatch(showModal(
          () => <ConfirmActionWarningModal description={inAppText.insuredProfile.documentsTable.deleteDocument} />,
          {
            title: 'Delete Document?',
            size: 'md',
            buttons: {
              component: StackedButtonGroup,
              primaryButtonOptions: {
                isDisabled: true,
                label: 'Delete',
                onClick: async () => {
                  await deleteDocument();

                  dispatch(hideModal());
                }
              }
            }
          }
        ));
      }
    }
  ];

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      key={document.id}
      width="200px"
    >
      <Tooltip
        aria-label={`Document Name: ${document.displayName}`}
        label={document.displayName}
      >
        <Text
          _hover={{ color: 'primaryScale.600', cursor: 'pointer' }}
          data-analytics-id={analyticsIds.insuredProfilePage.documentsComponent.downloadDocument}
          data-cy={CypressIds.documentFileNameCell(document.id)}
          noOfLines={1}
          pt={0}
          textStyle="button"
          onClick={() => handleDocClick(document.id)}
        >
          {document.displayName}
        </Text>
      </Tooltip>
      {!hideOptions && (
        <IconButtonMenu
          cypressId={CypressIds.documentOptionsButton(document.id)}
          dusk={`documents-option-dropdown_doc-id-${document.id}`}
          iconElement={<Icon as={BsChevronDown} size="md" />}
          menuOptions={menuOptions}
        />
      )}
    </Flex>
  );
};

FileNameCell.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.number,
    displayName: PropTypes.string,
    insurableId: PropTypes.number,
    insuranceDocumentTypeId: PropTypes.number
  }),
  recordId: PropTypes.number,
  hideOptions: PropTypes.bool
};

export default BaseDocumentCell(FileNameCell);
